import { useEffect, useState } from 'react';
import { api, type Brand, type Card, type Idea, type StartStep } from '../lib/api';
import { IdeaLab } from './IdeaLab';

// Start here: the first step the studio never had.
//
// Every screen was reachable and none of them was first. A new workspace
// landed on the Flight Deck, was told in prose to set a Brand kit, clicked
// Brand kit, and was told to choose a show that did not exist — and the only
// place to make one was the bottom of Settings. This is the missing door.
//
// It is a checklist rather than a tour on purpose: a tour has to be finished
// in one sitting and a checklist can be abandoned and picked up next week.
// Each row says what the creator gets, not what they have failed to do, and
// each row goes somewhere — the same setView plus focusMove pair the
// assistant director already uses, so a row lands on the bench with the right
// control lit rather than on a screen the creator then has to search.
//
// Making a show is offered here, inline, because its absence is the first
// wall and sending someone to Settings to get past it is the reason they
// never got past it.

const ORDER: Record<string, { n: string; title: string; verb: string }> = {
  show:        { n: '01', title: 'Make a show',            verb: 'Make one' },
  talent:      { n: '02', title: 'Add someone to read',    verb: 'Open Talent' },
  look:        { n: '03', title: 'Build the look',         verb: 'Open the Lab' },
  card:        { n: '04', title: 'Write one line',         verb: 'Open the board' },
  render:      { n: '05', title: 'Render it',              verb: 'Open the board' },
  destination: { n: '06', title: 'Choose where it goes',   verb: 'Open Connectors' },
};

export function StartHere({ brands, brand, cards, onGo, onBrandMade, onCardMade }: {
  brands: Brand[];
  brand: Brand | null;
  cards: Card[];
  onGo: (bench: string, move: string | null, prefill?: Idea | null) => void;
  onBrandMade: (b: Brand) => void;
  /** A project started here, so the studio's list knows about it at once. */
  onCardMade: (c: Card) => void;
}) {
  // Two states, and which one you get is the whole fix for "confusing".
  //
  // A workspace with no show gets the setup checklist, because a card belongs
  // to a brand and there is nothing to have ideas WITH. The moment a show
  // exists this screen is a blank page for a new idea, and the checklist
  // collapses to one line that reopens. Start Here is where an idea is born —
  // not a list of chores, and not a view of what is already under way, which
  // is what the board is for.
  const [setupOpen, setSetupOpen] = useState(false);
  const [steps, setSteps] = useState<StartStep[]>([]);
  const [done, setDone] = useState(0);
  const [next, setNext] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [making, setMaking] = useState(false);

  async function load() {
    try {
      const r = await api.startHere();
      setSteps(r.steps); setDone(r.done); setNext(r.next);
    } catch (e) { setError((e as Error).message); } finally { setLoaded(true); }
  }
  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [brands.length]);

  async function makeShow() {
    if (!name.trim()) return;
    // The same derivation Settings uses: the slug is the address the storage
    // keys are built from, so it is derived and never typed.
    const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
    if (!slug) { setError('That name has no letters or numbers in it.'); return; }
    setMaking(true); setError(null);
    try {
      const b = await api.createBrand(name.trim(), slug);
      setName(''); onBrandMade(b); await load();
    } catch (e) { setError((e as Error).message); } finally { setMaking(false); }
  }

  // The panel does not flash its empty state on every load, the same reason
  // the Flight Deck's first-run panel waits (FlightDeck.tsx:56).
  if (!loaded) return <section data-tool="start-here" style={{ minHeight: 240 }} />;

  // A workspace with no SHOW cannot make anything: a card belongs to a brand,
  // the cast reads for one, the look is one show's graphics. That is the only
  // reason to show a checklist instead of a blank page.
  //
  // It used to be "has this workspace any projects", which meant the checklist
  // came back every time the board emptied and, worse, that Start Here was a
  // list of finished chores rather than the place an idea is born.
  const working = brands.length > 0;
  if (working && !setupOpen) {
    return (
      <section data-tool="start-here" style={{ display: 'grid', gap: 14, maxWidth: 880, width: '100%' }}>
        {/* Constrained, so main's auto margins centre it; the Idea Lab inside
            centres itself within it. Without a width here the section filled
            the room and the column inside it hugged the left edge. */}
        {error && <p className="err" role="alert" style={{ margin: 0 }}>{error}</p>}
        <IdeaLab brand={brand} cards={cards} onGo={onGo} onError={setError} onCardMade={onCardMade} />
        <button type="button" className="btn tiny" data-tool="start-setup-reopen"
                style={{ justifySelf: 'center' }} onClick={() => setSetupOpen(true)}>
          {done === steps.length ? 'Setup — all done' : `Setup — ${done} of ${steps.length} done`}
        </button>
      </section>
    );
  }

  return (
    <section data-tool="start-here" style={{ display: 'grid', gap: 18, maxWidth: 820 }}>
      <div style={{ display: 'grid', gap: 6 }}>
        <div className="row" style={{ gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Start here</h3>
          <span className="sub-dim">{done === steps.length ? 'All six done — the rest is just the next episode.' : `${done} of ${steps.length} done`}</span>
        </div>
        <p className="sub-dim" style={{ margin: 0, maxWidth: '62ch' }}>
          Six things stand between an empty workspace and a published video. You only do the first three once; every episode after starts at the fourth.
        </p>
      </div>

      {error && <p className="err" role="alert" style={{ margin: 0 }}>{error}</p>}

      <div style={{ display: 'grid', gap: 8 }}>
        {steps.map((s) => {
          const meta = ORDER[s.id] ?? { n: '—', title: s.id, verb: 'Open' };
          const isNext = s.id === next;
          return (
            <div key={s.id} data-move={`start-${s.id}`}
                 style={{
                   display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', gap: 12,
                   alignItems: 'start', padding: 12, borderRadius: 6,
                   border: `1px solid var(${isNext ? '--accent' : '--line'})`,
                   background: isNext ? 'var(--sunk, transparent)' : 'transparent',
                   opacity: s.done ? 0.62 : 1,
                 }}>
              <span aria-hidden="true" style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, opacity: 0.55, paddingTop: 3 }}>
                {s.done ? '✓' : meta.n}
              </span>
              <div style={{ minWidth: 0, display: 'grid', gap: 3 }}>
                <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ textDecoration: s.done ? 'line-through' : 'none' }}>{meta.title}</strong>
                  {s.detail && <span className="sub-dim" style={{ fontSize: 12 }}>{s.detail}</span>}
                  {isNext && <span className="chip" style={{ fontSize: 11 }}>next</span>}
                </div>
                <span className="sub-dim" style={{ fontSize: 13, lineHeight: 1.45 }}>{s.why}</span>

                {/* The one step that has nowhere useful to send you, because the
                    screen it would send you to refuses to work without the
                    thing this step makes. So it is made here. */}
                {s.id === 'show' && !s.done && (
                  <div className="row" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <input value={name} maxLength={80} placeholder="What is the show called?"
                           onChange={(e) => setName(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter') void makeShow(); }}
                           style={{ flex: 1, minWidth: 180 }} />
                    <button type="button" className="btn pri" disabled={making || name.trim().length < 2} onClick={() => void makeShow()}>
                      {making ? 'Making…' : 'Make the show'}
                    </button>
                  </div>
                )}
              </div>
              {!(s.id === 'show' && !s.done) && (
                <button type="button" className={`btn tiny${isNext ? ' pri' : ''}`} onClick={() => onGo(s.bench, s.move)}>
                  {s.done ? 'Open' : meta.verb}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="sub-dim" style={{ margin: 0, fontSize: 12, maxWidth: '62ch' }}>
        Nothing here is a gate. Every screen is still in the sidebar, in the order you work in them.
      </p>
      {working && (
        <button type="button" className="btn tiny" style={{ justifySelf: 'start' }} onClick={() => setSetupOpen(false)}>
          Back to the Idea Lab
        </button>
      )}
    </section>
  );
}
